// Composer Studio — 미리보기 렌더 공용 헬퍼.
//
// 양식/작성 미리보기(EditorPage PreviewView)와 팝아웃 창(PreviewPopoutPage)이 동일한 렌더를
// 공유하도록, EditorPage 본체에 있던 순수 렌더 헬퍼를 이 모듈로 추출한다(동작 불변):
//   - mergePreviewContent: resolve content(select 경로 키)를 placeholder 위에 병합해 RenderData 산출
//   - applySimulatedApproval: approvalLineMeta로 시뮬레이션 결재란 주입(실데이터 우선)
//   - COMPOSER_SPECIAL_COMPONENTS: 캔버스/미리보기 special 컴포넌트 맵
//   - PagedEditBody: body를 A4 낱장으로 분할 렌더(표 행 분할·페이지 채움 포함)
//
// (도메인 로직 없음 — 순수 렌더/측정. EditorPage와 팝업 양쪽에서 import.)

import {
  RenderTemplate,
  resolveForRender,
  buildSimulatedApprovalData,
  usePagedPlan,
  buildPageChannels,
  buildSliceBody,
  pageContentHeight,
  ApprovalBlockSpecial,
  PageNumberLabel,
} from '@safetyhub/report-composer';
import type { BranchPaginationContext } from '@safetyhub/report-composer';
import type { ReportTemplate, RenderData, SpecialComponentMap } from '@safetyhub/report-composer';
import { placeholderData } from './sampleTemplate';
import { pageDimPx } from './pagedCanvas';

/**
 * Composer 캔버스/미리보기에 주입하는 special 컴포넌트 맵 (P1-2).
 * 결재란(approvalBlock)은 공유 ApprovalBlockSpecial로 렌더 — 실데이터 없으면 "담당" 1칸 합성.
 * (annotatedPhoto/signaturePad는 web 전용이라 admin Studio에서는 미주입 → placeholder)
 */
export const COMPOSER_SPECIAL_COMPONENTS: SpecialComponentMap = {
  approvalBlock: ApprovalBlockSpecial,
};

/**
 * 템플릿의 approvalLineMeta로 시뮬레이션 결재란(고정 단계 + 빈 동적 슬롯)을 data.approval에 주입한다.
 * 실데이터(data.approval 이미 존재)는 덮지 않는다(실데이터 우선). 빌더가 가벼워 모듈 순수 함수로 둠
 * (EditorPage 본체·PreviewView·팝업 양쪽에서 동일 사용).
 *
 * `approvalSignedLevel`(결재 서명 단계 모사 축 `__approvalSigned`, 2026-09-04): N이면 1..N 단계를
 * 표본 서명(APPROVED)으로 채운다. 미지정(기본)은 빈 결재란 그대로 — 모사의 산출이지 fallback이
 * 아니다(결정 정본). 서버는 이 축 키를 명시적으로 건너뛰므로 적용 지점은 여기(클라)뿐이다.
 */
export function applySimulatedApproval(
  template: ReportTemplate,
  data: RenderData,
  approvalSignedLevel?: number,
): RenderData {
  const meta = template.approvalLineMeta;
  if (!meta || data.approval) return data;
  return { ...data, approval: buildSimulatedApprovalData(meta, { signedLevel: approvalSignedLevel }) };
}

/**
 * preview-resolve content(= compose 스냅샷 형식, select 경로 키)를 placeholder 위에 병합한다.
 *
 * RenderTemplate은 bindingKey로 읽으므로 content를 그대로 spread하면 select 경로 키(예: 'site.name')가
 * 노드의 bindingKey(예: 'siteName')와 어긋나 실값이 안 보인다. resolveForRender로 template을 거쳐
 * bindingKey 키 RenderData로 변환한 뒤, **resolve된(정의된) 키만** placeholder에 덮어쓴다
 * (미해석/레거시 노드는 undefined → placeholder 샘플값 유지).
 */
export function mergePreviewContent(
  template: ReportTemplate,
  content: Record<string, unknown>,
): RenderData {
  // liveData에도 content를 전달 — 미리보기 content는 서버가 방금 resolve한 현재값이므로
  // live lifecycle 바인딩(서명 등)도 이 값으로 읽는 게 맞다(미전달 시 live 바인딩은 전부
  // undefined로 떨어져 미리보기에서만 빈값이 되는 함정).
  const rd = resolveForRender(template, content, undefined, content);
  const merged: RenderData = { ...placeholderData };
  for (const [k, v] of Object.entries(rd)) {
    if (v !== undefined) merged[k] = v;
  }
  return merged;
}

/**
 * Word식 여백-본문 경계 마크 — 각 A4 낱장의 본문 영역(여백 안쪽) 네 모서리에 ┌ ┐ └ ┘ 코너 브래킷.
 * 낱장 좌표(px)로 그려 scaler(zoom)와 함께 스케일된다. pointer-events:none이라 편집 방해 없음.
 */
function MarginCorners({
  margins,
}: {
  margins: { top: number; right: number; bottom: number; left: number };
}) {
  const L = 16; // 코너 마크 변 길이(px, 794 원본 좌표)
  const line = '1px solid #9ca3af'; // gray-400
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', width: L, height: L, top: margins.top, left: margins.left, borderTop: line, borderLeft: line }} />
      <div style={{ position: 'absolute', width: L, height: L, top: margins.top, right: margins.right, borderTop: line, borderRight: line }} />
      <div style={{ position: 'absolute', width: L, height: L, bottom: margins.bottom, left: margins.left, borderBottom: line, borderLeft: line }} />
      <div style={{ position: 'absolute', width: L, height: L, bottom: margins.bottom, right: margins.right, borderBottom: line, borderRight: line }} />
    </div>
  );
}

/**
 * (A안) 편집 캔버스 body를 A4 낱장으로 분할해 렌더. 노드 경계 + **표 행 분할**(표가 페이지에 걸치면
 * 안 잘리는 행까지 채우고 나머지 행은 다음 낱장으로 반복). 측정은 렌더된 DOM에서: 노드 높이
 * (offsetHeight) + 표 각 행(data-composer-row)의 높이·rowspan(td.rowSpan+closest tr). 안전 경계
 * (computeSafeCutRows) 밖에선 자르지 않는다. 같은 표를 여러 낱장에 서로 다른 rowRange로 렌더해도 셀
 * 좌표가 원본이라(반복 전개 제외) 중복이 없어 단일 캔버스 오버레이 편집이 낱장 위치 무관 동작(스파이크
 * 선검증). 배치(정수 행/페이지)가 바뀔 때만 setState해 수렴한다(높이 float 노이즈는 plan에 안 들어감).
 *
 * 그 측정·수렴 배선 자체는 **rc `usePagedPlan` 훅이 소유한다**(아래 호출부) — 이 파일에 남은 것은
 * JSX와 `branchOpts` 조립이다. 종전엔 같은 배선이 이 함수와 rc `PagedComposerView`에 **두 벌**이었다.
 */
// 측정·계획 순수 함수(pagePlansEqual·planEditPages·measureBody 등)는 전부 rc가 소유하고, 이제는
// 그 호출조차 호스트가 하지 않는다 — `usePagedPlan` 하나가 든다(그 헤더가 정본).
//
// `onPageCount`: 낱장 수 통지 — rc `PagedComposerView`가 가진 같은 이름·같은 형태의 prop을 미러한다
// (PDF 캡처가 그것으로 print-meta를 맞춘다). 여기 필요한 이유는 다르다: **용지 설정 패널의 페이지번호
// 경고**(「출력 시작이 전체 장수보다 커서 아무 낱장에도 안 찍힌다」)가 현재 장수를 알아야 한다.
// 엔진이 조용히 skip하는 것을 화면이 숨기면 저작자는 켰다고 믿는다.
export function PagedEditBody(
  props: React.ComponentProps<typeof RenderTemplate> & {
    /** 낱장 배치 확정 시 장수 통지(용지 설정 패널 경고용). */
    onPageCount?: (n: number) => void;
  },
) {
  // onPageCount는 RenderTemplate의 prop이 아니므로 분리한다 — 아래 `{...renderProps}` 스프레드에
  // 섞이면 안 된다. 나머지는 **손으로 고르지 않고 그대로** 넘긴다(필드를 골라 담으면 RenderContext에
  // 판정 축이 늘었을 때 렌더는 새 축을 받고 계획만 못 받아 조용히 갈라진다 — 아래 branchOpts 주석).
  const { onPageCount, ...renderProps } = props;
  const { template } = renderProps;
  const body = template.body;
  const dim = pageDimPx(template.page);
  const pageContentH = pageContentHeight(template.page);
  // 최상위 branch 투명 통과 입력 — 편집 캔버스는 조건 평가 대신 탭 명시 선택(activeBranchSlots)을
  // 쓰므로 그대로 넘긴다(렌더 RenderTemplate과 같은 판정). 통짜 plan·측정·slice가 이 하나를 공유.
  // props를 그대로 넘긴다 — 필드를 손으로 골라 담으면 RenderContext에 판정 축이 늘었을 때
  // 렌더(`{...renderProps}`)는 새 축을 받고 계획만 못 받아 조용히 갈라진다.
  const branchOpts: BranchPaginationContext = renderProps;
  // 낱장 배치 파이프라인(state 초기화·수렴 가드·클립 리셋·측정 effect·RO 구독·장수 통지·EPS)은 rc
  // 공용 훅 하나가 소유한다 — rc `PagedComposerView`가 **같은 훅**을 부른다. 종전엔 이 배선이 두
  // 래퍼에 **두 벌**이었다(2026-08-19 `/code-review-full` 지적). 사본이면 조용히 갈라지고, 이
  // 파이프라인의 결함은 전부 진동·고착이라 갈린 날의 증상은 크래시가 아니라 **「한쪽 화면에서만
  // 내용이 사라지는 것」**이다 — 사람이 알아차리지 못한다. 그 흐름을 고치려면 여기가 아니라 훅을
  // 보고, `usePagedPlan` 헤더가 「무엇을 호스트가 소유하는가」의 정본이다.
  //
  // ⚠️ 반환값은 **반드시 `pagePlan`이라는 이름으로** 구조분해한다 — 아래 `PageNumberLabel` 호출부가
  // rc 래퍼와 **글자 그대로 같아야** 하고 이 파일의 spec이 그것을 소스로 재기 때문이다
  // (`pagedEditBodyPageNumber.test.tsx` `[되돌림] 호출부가 rc 래퍼와 글자 그대로 같다`).
  const { containerRef, pagePlan } = usePagedPlan({ body, pageContentH, branchOpts, onPageCount });

  return (
    <div
      ref={containerRef}
      style={{ display: 'flex', flexDirection: 'column', gap: 24, width: dim.w }}
    >
      {pagePlan.map((items, i) => {
        // 채널 맵 구성은 rc buildPageChannels(순수) 하나로 — 키가 인스턴스 경로(pagedItemKey)라
        // 중첩 반복의 인스턴스별 지시가 덮어써지지 않는다. body slice는 최상위 항목만(중첩 지시는
        // 노드가 아니라 부모 서브트리 안에서 소비된다).
        const {
          topLevel,
          rowRanges,
          itemRanges,
          chunkRanges,
          leadSuppressed,
          fillHeights,
          fillSlacks,
        } = buildPageChannels(items);
        // 하강한 branch 자식은 buildSliceBody가 그 branch로 다시 감싼다 — wrapper를 유지해야
        // 캔버스 선택(data-composer-node-id로 branch 노드 역추적)이 그대로 동작한다.
        const sliceBody = buildSliceBody(topLevel, body, branchOpts);
        return (
          <div
            key={i}
            className="shadow-lg"
            style={{ width: dim.w, minHeight: dim.h, background: '#fff', position: 'relative' }}
          >
            <RenderTemplate
              {...renderProps}
              template={{ ...template, body: sliceBody }}
              rowRanges={rowRanges}
              itemRanges={itemRanges}
              // 순차 다경계 청크 창(2026-09-04) — 미배선 시 다경계 단위가 조각마다 통째로
              // 중복 렌더된다(rc PagedComposerView와 같은 이음새).
              chunkRanges={chunkRanges}
              leadSuppressed={leadSuppressed}
              fillHeights={fillHeights}
              fillSlacks={fillSlacks}
            />
            {/* 문서 페이지번호 — rc `PagedComposerView`(상세·인쇄·PDF·공유·외부 서명)가 렌더하는
                것과 **같은 조각을 같은 인자로** 부른다. 사본을 만들면 갈리고, 갈린 날 캔버스가
                출력과 다른 번호를 보여 준다 — 그것이 이 기능이 막으려는 사고 그 자체다.
                ⚠️ MarginCorners와 달리 **캔버스 전용이 아니다**(출력물의 일부다). */}
            <PageNumberLabel page={template.page} pageIndex={i} pageCount={pagePlan.length} />
            <MarginCorners margins={template.page.margins} />
          </div>
        );
      })}
    </div>
  );
}
